import { useState } from "react";

import { AdminButton } from "@/components/admin/button";
import { FormRow } from "@/components/admin/form";
import { useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import {
	useConvertToSimple,
	useSimpleConversionPreview,
} from "@/hooks/use-catalog";

interface Props {
	productId: string;
	variants: Array<{ id: string; name: string; sku: string }>;
	/** Déclinaison présélectionnée : celle par défaut du produit. */
	defaultVariantId: string | null;
	onClose: () => void;
	/** Appelé après une conversion réussie, avant la fermeture. */
	onConverted?: () => void;
}

const unites = (n: number) => `${n} unité${n > 1 ? "s" : ""}`;

/**
 * Passage d'un produit à déclinaisons en produit simple.
 *
 * Le serveur calcule l'aperçu (stock cumulé par entrepôt, déclinaisons
 * supprimées ou désactivées, blocages) : la fenêtre ne fait que l'afficher,
 * pour que ce qui est annoncé soit exactement ce qui sera fait.
 */
export function ConvertToSimpleModal({
	productId,
	variants,
	defaultVariantId,
	onClose,
	onConverted,
}: Props) {
	const toast = useToast();
	const [gardee, setGardee] = useState(
		variants.some((v) => v.id === defaultVariantId)
			? (defaultVariantId as string)
			: (variants[0]?.id ?? ""),
	);
	const apercu = useSimpleConversionPreview(productId, gardee || undefined);
	const conversion = useConvertToSimple(productId);
	const [erreur, setErreur] = useState<string | null>(null);

	const plan = apercu.data;
	const bloque = !plan || plan.blocages.length > 0;

	const valider = async () => {
		setErreur(null);
		try {
			await conversion.mutateAsync(gardee);
			onConverted?.();
			toast.success(
				plan
					? `Produit simple — ${unites(plan.stockTotal)} en stock.`
					: "Produit passé en produit simple.",
			);
			onClose();
		} catch (e) {
			setErreur(extractErrorMessage(e, "Conversion impossible"));
		}
	};

	return (
		<Modal
			title="Passer en produit simple"
			subtitle={`${variants.length} déclinaisons → une seule référence en vente`}
			onClose={onClose}
			width={520}
		>
			<FormRow
				label="Déclinaison gardée"
				required
				helper="Son SKU et son prix deviennent ceux du produit. Elle prend le nom « Standard »."
			>
				<select
					className="aform-i"
					value={gardee}
					onChange={(e) => setGardee(e.target.value)}
					data-testid="convert-simple-keep"
				>
					{variants.map((v) => (
						<option key={v.id} value={v.id}>
							{v.name} — {v.sku}
						</option>
					))}
				</select>
			</FormRow>

			{apercu.isLoading && <p className="muted conv-note">Calcul de l'aperçu…</p>}
			{apercu.isError && (
				<div role="alert" className="conv-alerte">
					{extractErrorMessage(apercu.error, "Aperçu indisponible")}
				</div>
			)}

			{plan && (
				<div className="conv-apercu">
					<div className="conv-total">
						<strong>{unites(plan.stockTotal)}</strong>
						<span className="muted">stock cumulé de toutes les déclinaisons</span>
					</div>
					{plan.stockParHub.length > 0 && (
						<ul className="conv-hubs">
							{plan.stockParHub.map((l) => (
								<li key={l.hubId}>
									<span>{l.hubName ?? "Entrepôt"}</span>
									<span className="mono">
										{l.avant} → <strong>{l.apres}</strong>
									</span>
								</li>
							))}
						</ul>
					)}
					{plan.supprimees.length > 0 && (
						<p className="conv-note">
							<strong>Supprimées :</strong>{" "}
							{plan.supprimees.map((v) => v.name).join(", ")}
						</p>
					)}
					{plan.desactivees.length > 0 && (
						<p className="conv-note">
							<strong>Désactivées</strong> (citées par des commandes, gardées pour
							l'historique) : {plan.desactivees.map((v) => v.name).join(", ")}
						</p>
					)}
					<p className="conv-note muted">
						Leurs photos rejoignent la galerie du produit ; les axes d'options
						(couleur, taille…) sont retirés.
					</p>
				</div>
			)}

			{plan && plan.blocages.length > 0 && (
				<div role="alert" className="conv-alerte">
					<strong>Conversion impossible pour l'instant :</strong>
					<ul>
						{plan.blocages.map((b) => (
							<li key={b}>{b}</li>
						))}
					</ul>
				</div>
			)}

			<p className="conv-avert">
				Action définitive : pour revenir en arrière, il faudra recréer les
				déclinaisons à la main.
			</p>

			{erreur && (
				<div role="alert" className="conv-alerte">
					{erreur}
				</div>
			)}

			<ModalFooter>
				<AdminButton
					variant="ghost"
					onClick={onClose}
					disabled={conversion.isPending}
				>
					Revenir
				</AdminButton>
				<AdminButton
					variant="primary"
					onClick={() => void valider()}
					disabled={conversion.isPending || bloque || apercu.isFetching}
					data-testid="convert-simple-confirm"
				>
					{conversion.isPending ? "Conversion…" : "Passer en produit simple"}
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
